import { FilterOptions } from '@app/components/data-table/hooks/use-server-filter-options';
import { BusinessTravelActivitiesList } from '@shared/api/types';
import Unit from '@shared/components/content/unit';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import { capitalizeFirstLetter, formatQuantity } from '@shared/lib/utils';
import { ColumnDef } from '@tanstack/react-table';
import {
  BUSINESS_TRAVEL_METHODOLOGY,
  BusinessTravelMethodology,
  METHODOLOGY_FILTER_OPTIONS,
  METHODOLOGY_LABELS,
} from './constants';

const EMPTY_CELL = '-';

const methodologyLabel = (raw: string | null | undefined): string => {
  if (!raw) return EMPTY_CELL;
  return (
    METHODOLOGY_LABELS[raw as BusinessTravelMethodology] ??
    capitalizeFirstLetter(raw)
  );
};

const sourceLabel = (row: BusinessTravelActivitiesList): string => {
  if (row.inputMethodology === BUSINESS_TRAVEL_METHODOLOGY.fuelUsage) {
    return row.fuel?.name ?? EMPTY_CELL;
  }
  if (row.inputMethodology === BUSINESS_TRAVEL_METHODOLOGY.passengerTransport) {
    const mode = row.passengerTransportMode;
    if (!mode) return EMPTY_CELL;
    return [mode.mode, mode.type, mode.subtype]
      .filter((part): part is string => !!part)
      .map(capitalizeFirstLetter)
      .join(' - ');
  }
  return EMPTY_CELL;
};

export function useBusinessTravelTableColumns({
  filters,
}: {
  filters: FilterOptions<['timePeriods', 'sites', 'fuels']>;
}): ColumnDef<BusinessTravelActivitiesList>[] {
  return [
    {
      id: 'uuid',
      accessorKey: 'uuid',
      enableHiding: true,
      enableColumnFilter: true,
    },
    {
      id: 'includeSecr',
      accessorKey: 'includeSecr',
      enableHiding: true,
      enableColumnFilter: true,
      enableSorting: false,
      meta: {
        label: 'Show only SECR',
        variant: 'boolean',
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'period',
      accessorKey: 'period',
      enableHiding: true,
      enableColumnFilter: true,
      meta: {
        label: 'Time period',
        variant: 'multiSelect',
        options: filters.timePeriods,
        sorting: { ascLabel: 'Oldest', descLabel: 'Newest' },
      },
    },
    {
      id: 'site',
      accessorKey: 'site',
      enableColumnFilter: true,
      header: ({ column }) => <HeaderCell column={column} name="Site" />,
      cell: ({ getValue }) => {
        const site = getValue<BusinessTravelActivitiesList['site']>();
        return site ? (
          <TextCell text={site.name} />
        ) : (
          <TextCell text={EMPTY_CELL} />
        );
      },
      meta: {
        label: 'Site',
        variant: 'multiSelect',
        options: filters.sites,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'methodology',
      accessorKey: 'inputMethodology',
      enableColumnFilter: true,
      header: ({ column }) => <HeaderCell column={column} name="Methodology" />,
      cell: ({ getValue }) => (
        <TextCell text={methodologyLabel(getValue<string | null>())} />
      ),
      meta: {
        label: 'Methodology',
        variant: 'multiSelect',
        options: METHODOLOGY_FILTER_OPTIONS,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'source',
      header: ({ column }) => <HeaderCell column={column} name="Source" />,
      cell: ({ row }) => <TextCell text={sourceLabel(row.original)} />,
      enableSorting: false,
    },
    {
      accessorKey: 'quantity',
      header: ({ column }) => (
        <HeaderCell column={column} name="Quantity" numberColumn />
      ),
      cell: ({ row, getValue }) => {
        if (
          row.original.inputMethodology ===
          BUSINESS_TRAVEL_METHODOLOGY.totalEmissions
        ) {
          return <TextCell text={EMPTY_CELL} type="number" />;
        }
        return (
          <NumberCell number={getValue<number>()} formatter={formatQuantity} />
        );
      },
    },
    {
      id: 'unit',
      accessorKey: 'unit',
      header: ({ column }) => <HeaderCell column={column} name="Unit" />,
      cell: ({ getValue }) => {
        const unit = getValue<BusinessTravelActivitiesList['unit']>();
        return <TextCell text={unit?.symbol ?? EMPTY_CELL} />;
      },
      enableSorting: false,
    },
    {
      accessorKey: 'emissionFactor',
      header: ({ column }) => (
        <HeaderCell column={column} name="Emission Factor" numberColumn />
      ),
      cell: ({ row }) => {
        const r = row.original;
        if (r.inputMethodology === BUSINESS_TRAVEL_METHODOLOGY.totalEmissions) {
          return <TextCell text={EMPTY_CELL} type="number" />;
        }
        if (r.emissionFactor == null)
          return <TextCell text={EMPTY_CELL} type="number" />;
        const unitSymbol = r.unit?.symbol;
        const suffix = unitSymbol ? `kgCO2e/${unitSymbol}` : 'kgCO2e/unit';
        return (
          <span className="flex items-baseline justify-end gap-1">
            <NumberCell number={r.emissionFactor} />
            <span className="text-xs text-gray-500">{suffix}</span>
          </span>
        );
      },
    },
    {
      accessorKey: 'emissions',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emissions"
          numberColumn
          unit={<Unit variant="tCO2e" />}
        />
      ),
      cell: ({ getValue }) => <NumberCell number={getValue<number>()} />,
    },
  ];
}
